"use client";
import { useTranslations } from "next-intl";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { GoogleMapComponent } from "@/components/ui-components/google-map";
import { useIsMobileLandscape } from "@/hooks/use-mobile-landscape";
import { MapLocation } from "@/types/location";
import { CONTACT } from "@/lib/constants/contact";
import { SECTION_CONTAINER, SECTION_HEADING, SECTION_PADDING } from "@/lib/home-section-layout";

// Location data is not translatable — store names, addresses and hours are fixed
const DEFAULT_LOCATIONS: MapLocation[] = [
  {
    id: "miami",
    name: "Car Care Center Bird Road",
    phone: CONTACT.phones.primary,
    fullAddress: "6795 SW 40th St, Miami, FL 33155, USA",
    position: { lat: 25.7397, lng: -80.3195 },
    hours: {
      mondayFriday: "07:00 AM - 06:00 PM",
      saturday: "08:00 AM - 04:00 PM",
      sunday: "Closed",
    },
    services: ["Oil Change", "Tire Services", "Brake Repair", "Diagnostics"],
  },
  {
    id: "calle-8",
    name: "Car Care Center Calle 8",
    // NOTE (WSP-832): intentionally on the primary line to preserve what this
    // surface shows today. The two location data sources disagree on this
    // store's number (app/locations/page.tsx uses phones.calleOcho); which is
    // correct is a data-truth call pending Will, not an oversight here.
    phone: CONTACT.phones.primary,
    fullAddress: "1400 SW 8th St, Miami, FL 33135, USA",
    position: { lat: 25.7649, lng: -80.2162 },
    hours: {
      mondayFriday: "07:00 AM - 06:00 PM",
      saturday: "08:00 AM - 04:00 PM",
      sunday: "Closed",
    },
    services: ["Oil Change", "Tire Services", "Brake Repair", "Diagnostics"],
  },
  {
    id: "collision-center",
    name: "Collision Center",
    phone: CONTACT.phones.collision,
    fullAddress: "7350 SW 42nd St, Miami, FL 33155, USA",
    position: { lat: 25.7372, lng: -80.3228 },
    hours: {
      mondayFriday: "07:00 AM - 06:00 PM",
      saturday: "Closed",
      sunday: "Closed",
    },
    services: ["Body Repair", "Paint Matching", "Frame Straightening"],
  },
];

interface LocationsSectionProps {
  className?: string;
  sectionTitle?: string;
  sectionSubtitle?: string;
  hoursTitle?: string;
  mondayLabel?: string;
  saturdayLabel?: string;
  sundayLabel?: string;
  directionsLabel?: string;
  locations?: MapLocation[];
}

interface LocationDotsProps {
  locations: MapLocation[];
  activeLocation: number;
  onSelect: (index: number) => void;
}

// Rendered twice — once in the desktop control cluster, once in the mobile one.
// Kept as a single component rather than inline markup so the two can't drift:
// the enlarged target below previously landed on the desktop copy alone.
function LocationDots({
  locations,
  activeLocation,
  onSelect,
}: LocationDotsProps) {
  return (
    <div className="flex">
      {locations.map((location, index) => (
        <button
          key={index}
          type="button"
          aria-label={`Go to ${location.name} location`}
          aria-current={index === activeLocation ? "true" : undefined}
          onClick={() => onSelect(index)}
          className="group grid h-11 w-8 place-items-center focus-visible:outline-none"
        >
          {/* 12px dot inside a 32×44px thumb target, buttons touching so the
              dots keep their 32px pitch */}
          <span
            aria-hidden="true"
            className={cn(
              "block w-3 h-3 rounded-full transition-colors group-focus-visible:ring-2 group-focus-visible:ring-yellow-500 group-focus-visible:ring-offset-4 group-focus-visible:ring-offset-site-card",
              index === activeLocation ? "bg-yellow-500" : "bg-white/30"
            )}
          />
        </button>
      ))}
    </div>
  );
}

export function LocationsSection({
  className,
  locations = DEFAULT_LOCATIONS,
}: LocationsSectionProps) {
  const t = useTranslations('locations');
  const isMobileLandscape = useIsMobileLandscape();
  const [activeLocation, setActiveLocation] = useState(0);
  const canGoPrev = true; // Always allow cycling
  const canGoNext = true; // Always allow cycling

  const handlePrev = () => {
    setActiveLocation((prev) => (prev === 0 ? locations.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveLocation((prev) => (prev === locations.length - 1 ? 0 : prev + 1));
  };

  const currentLocation = locations[activeLocation];

  return (
    <section id="locations" role="region" aria-label="Miller40 service locations and map" data-surface="raised" className={cn(SECTION_PADDING, "font-outfit bg-site-card", className)}>
      <div className={cn(SECTION_CONTAINER, isMobileLandscape && "!mx-6")}>
        <div
          className={cn(
            "grid grid-cols-1 md:grid-cols-2 gap-12",
            isMobileLandscape && "!grid-cols-1 "
          )}
        >
          {/* Left side - Location info */}
          <div>
            <h2 className={SECTION_HEADING}>
              {t('sectionTitle')}
            </h2>
            <p className="text-white mb-8">{t('sectionSubtitle')}</p>

            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-yellow-500 mb-2">
                  {currentLocation.name as string}
                </h3>
                {currentLocation.phone && (
                  <a
                    href={currentLocation.phone.telHref}
                    data-phone={currentLocation.phone.key}
                    className="text-yellow-500 hover:underline font-semibold"
                  >
                    {currentLocation.phone.display}
                  </a>
                )}

                <p className="text-white mb-4 mt-1">
                  {currentLocation.fullAddress}
                </p>
              </div>

              <div className="hidden lg:flex items-center space-x-4 ml-6">
                <button aria-label="Goto previous location"
                  onClick={handlePrev}
                  disabled={!canGoPrev}
                  className={cn(
                    "rounded-full w-12 h-12 flex items-center justify-center text-black transition-colors",
                    canGoPrev
                      ? "bg-yellow-500 hover:bg-yellow-600"
                      : "bg-yellow-500/50 cursor-not-allowed"
                  )}
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>

                <LocationDots
                  locations={locations}
                  activeLocation={activeLocation}
                  onSelect={setActiveLocation}
                />

                <button  aria-label="Goto next location"
                  onClick={handleNext}
                  disabled={!canGoNext}
                  className={cn(
                    "rounded-full w-12 h-12 flex items-center justify-center text-black transition-colors",
                    canGoNext
                      ? "bg-yellow-500 hover:bg-yellow-600"
                      : "bg-yellow-500/50 cursor-not-allowed"
                  )}
                >
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="bg-zinc-900 font-outfit rounded-3xl p-6 md:p-8 mb-6">
              <h4 className="text-2xl font-semibold text-white mb-6">
                {t('hoursTitle')}
              </h4>
              <div className="space-y-3">
                <div className="flex gap-1 ">
                  <span className="text-white">{t('monday')}</span>{" "}
                  <span className="text-yellow-500">{currentLocation.hours?.mondayFriday}</span>
                </div>
                <div className="flex gap-1 ">
                  <span className="text-white">{t('saturday')}</span>{" "}
                  <span className="text-yellow-500">{currentLocation.hours?.saturday}</span>
                </div>
                <div className="flex gap-1 ">
                  <span className="text-white">{t('sunday')}</span>
                  <span className="text-yellow-500">{currentLocation.hours?.sunday}</span>
                </div>
              </div>
            </div>

            {/* Mobile navigation controls */}
            <div className="flex lg:hidden items-center justify-center space-x-4">
              <button aria-label="Goto previous location"
                onClick={handlePrev}
                disabled={!canGoPrev}
                className={cn(
                  "rounded-full w-12 h-12 flex items-center justify-center text-black transition-colors",
                  canGoPrev
                    ? "bg-yellow-500 hover:bg-yellow-600"
                    : "bg-yellow-500/50 cursor-not-allowed"
                )}
              >
                <ArrowLeft className="h-5 w-5" />
              </button>

              <LocationDots
                locations={locations}
                activeLocation={activeLocation}
                onSelect={setActiveLocation}
              />

              <button aria-label="Goto next location"
                onClick={handleNext}
                disabled={!canGoNext}
                className={cn(
                  "rounded-full w-12 h-12 flex items-center justify-center text-black transition-colors",
                  canGoNext
                    ? "bg-yellow-500 hover:bg-yellow-600"
                    : "bg-yellow-500/50 cursor-not-allowed"
                )}
              >
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Right side - Map */}
          <div className="relative rounded-3xl overflow-hidden h-[450px]">
            <GoogleMapComponent
              locations={locations}
              activeLocationId={currentLocation.id}
              darkMode={true}
            />
            <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-10">
              <Button
                onClick={() => {
                  const { lat, lng } = currentLocation.position;
                  window.open(
                    `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`,
                    "_blank"
                  );
                }}
                className="bg-black/50 backdrop-blur-sm border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black rounded-full px-6"
                size="sm"
              >
                <MapPin className="h-4 w-4 mr-2" />
                {t('directions')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
